import React, { useState } from 'react';
import { apiService } from '../services/api';
import { Itinerary, ItineraryCreate, ItineraryUpdate } from '../types';
import { ItineraryList } from './ItineraryList';
import { ItineraryForm } from './ItineraryForm';
import { useLanguage } from '../contexts/LanguageContext';

type ViewMode = 'list' | 'create' | 'edit';

export const ItineraryManagement: React.FC = () => {
  const { t } = useLanguage();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedItinerary, setSelectedItinerary] = useState<Itinerary | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = () => {
    setSelectedItinerary(undefined);
    setViewMode('create');
    setError(null);
  };

  const handleEdit = (itinerary: Itinerary) => {
    setSelectedItinerary(itinerary);
    setViewMode('edit');
    setError(null);
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await apiService.deleteItinerary(id);
      // The ItineraryList component will handle refreshing the list
    } catch (err) {
      setError('Failed to delete itinerary');
      console.error('Error deleting itinerary:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: ItineraryCreate | ItineraryUpdate) => {
    try {
      setLoading(true);
      setError(null);

      if (viewMode === 'create') {
        await apiService.createItinerary(data as ItineraryCreate);
      } else if (viewMode === 'edit' && selectedItinerary) {
        await apiService.updateItinerary(selectedItinerary.id, data as ItineraryUpdate);
      }

      setViewMode('list');
    } catch (err) {
      setError(viewMode === 'create' ? t('itineraries.failedToCreate') : t('itineraries.failedToUpdate'));
      console.error('Error saving itinerary:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setViewMode('list');
    setSelectedItinerary(undefined);
    setError(null);
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={() => setError(null)}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {viewMode === 'list' && (
        <ItineraryList
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCreate={handleCreate}
        />
      )}

      {(viewMode === 'create' || viewMode === 'edit') && (
        <div>
          <div className="mb-6">
            <button
              onClick={handleCancel}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              ← {t('itineraries.backToItineraries')}
            </button>
          </div>
          
          <ItineraryForm
            itinerary={selectedItinerary}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};
